import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useMutation } from 'react-query'
import { useParams } from 'react-router-dom'

import { getCurrentCandidate } from '@/CandidateExperience/selectors/candidate'
import useFeatureFlags from '@/CandidateExperience/hooks/useFeatureFlags'
import FeatureFlag from '@/CandidateExperience/constants/featureFlagConstants'

import useRecommendationFeedbackBannerVisibility from './useRecommendationFeedbackBannerVisibility'
import { createPositionFeedback } from './api'

/**
 * Custom hook for managing the props to be passed to RecommendationFeedbackBanner/BannerContent.js
 *
 * @returns {Object} The recommendation feedback banner state and actions.
 * @property {string} bannerTheme - The theme of the banner. Can be 'success' or 'dark'.
 * @property {boolean} isBannerVisible - Indicates whether the banner is visible or not.
 * @property {boolean} isFeedbackSubmitted - Indicates whether the feedback is successfully submitted or not.
 * @property {boolean} isSubmittingFeedback - Indicates whether the feedback is being submitted or not.
 * @property {function} submitFeedback - Function to submit the feedback.
 * @property {function} isValidRecommendation - Indicates whether the recommendation was good or bad
 *
 * @example
 *  const { isFeedbackSubmitted, submitFeedback, ...props } = useRecommendationFeedbackBanner()
 *
 *  return (
 *    <BannerContent
 *      isFeedbackSubmitted={isFeedbackSubmitted}
 *      submitFeedback={submitFeedback}
 *      ...props
 *    />
 *  )
 */
const useRecommendationFeedbackBanner = () => {
  const { id: candidateProfileId } = useSelector(getCurrentCandidate)
  const { jobId } = useParams()

  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false)
  const [isValidRecommendation, setIsValidRecommendation] = useState(false)

  const { isLoading: isLoadingFeatureFlags, data: featureFlags } = useFeatureFlags()
  const isCandidateDSRecommendationsEnabled = featureFlags?.includes(FeatureFlag.DSRecommendedPositions)

  const { isVisible } = useRecommendationFeedbackBannerVisibility()

  const { mutate: submitPositionFeedback, isLoading: isSubmittingFeedback } = useMutation(
    'createPositionFeedback',
    props => createPositionFeedback({ candidateProfileId, positionId: jobId, validRecommendation: props?.validRecommendation === 'yes' }),
    {
      onSuccess: data => {
        setIsFeedbackSubmitted(true)
        setIsValidRecommendation(data?.createRecommendedPositionFeedback?.feedback?.validRecommendation)
      },
      onError: (_, variables) => {
        variables?.onError()
      },
    },
  )

  return {
    bannerTheme: isFeedbackSubmitted ? 'success' : 'dark',
    isBannerVisible: isLoadingFeatureFlags || !isCandidateDSRecommendationsEnabled ? false : isVisible,
    isSubmittingFeedback,
    isFeedbackSubmitted,
    isValidRecommendation,
    submitFeedback: submitPositionFeedback,
  }
}

export default useRecommendationFeedbackBanner
